import { useLang } from 'hooks/lang';

import { actions, FullProjectTask, ProjectFile } from 'entities';

import { DropdownMenu, DropdownMenuItem } from 'components/Blocks';
import { EntityStatusTag } from 'components/Entities';
import { SwapIcon } from 'components/Icons';

import { useProjectProfile } from '../../context';
import { useConfirmation } from 'providers/Confirmation';

import { sortByName } from 'utils/array';

import { fixFileName } from 'utils/text';

interface SwapFileButtonProps {
  children?: never;
  file: ProjectFile;
}

export function SwapFileButton({ file }: SwapFileButtonProps) {
  const { updateProject, project } = useProjectProfile();
  const { requestConfirmation } = useConfirmation();

  const { t } = useLang();

  const swapFile = actions.projects.swapProjectFileToTask.use({
    block: file.restricted,

    onSuccess: (_, { fileId }) => {
      updateProject((old) => ({
        ...old,
        files: old.files.filter((file) => file.id !== fileId),
      }));
    },
  });

  const confirmSwap = (task: FullProjectTask) => {
    requestConfirmation({
      title: t('swap-files')!,

      type: 'irreversible',

      text: (
        <span className="text-center">
          <strong>{fixFileName(file.name)}</strong> {t('will-be-transferred-to')}{' '}
          <strong>{task.name}</strong>
        </span>
      ),

      onConfirm: async () => {
        await swapFile.execute({
          fileId: file.id,
          project: project.id,
          task: task.id,
        });
      },
    });
  };

  if (file.restricted) return null;

  return (
    <DropdownMenu
      title={t('move-to')}
      tooltipContent={t('move-to')}
      triggerIcon={<SwapIcon size={16} />}
      align="end"
    >
      {sortByName(project.tasks).map((task) => (
        <DropdownMenuItem key={task.id} onClick={() => confirmSwap(task)}>
          <EntityStatusTag target="task" status={task.status} round={false} />

          {task.name}
        </DropdownMenuItem>
      ))}
    </DropdownMenu>
  );
}
